import React, { useState, useMemo } from "react";
import { 
    Breadcrumb, 
    Layout, 
    Card, 
    Row, 
    Col, 
    Button, 
    theme, 
    Spin, 
    Alert,
    Input, 
    Table,
    Space,
    Dropdown,
    Tag,
    Tooltip,
    Empty,
    Avatar,
    Typography
} from "antd";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import FeatureUpgradeModal from "../components/FeatureUpgradeModal";
import EditBrandModal from "../components/Brand/EditBrandModal";
import { 
    EyeOutlined, 
    BarChartOutlined, 
    SearchOutlined,
    MoreOutlined,
    EditOutlined,
    DeleteOutlined,
    TableOutlined,
    AppstoreOutlined,
    CalendarOutlined,
    BuildOutlined
} from "@ant-design/icons";
import { useGetBrandsQuery } from "../services/nestjs-base-api";
import { IBrand } from "../services/nestjs-base-api/types";
import type { ColumnsType } from 'antd/es/table';

const { Content } = Layout;
const { Text, Title } = Typography;
const { Search } = Input;

const BrandsPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const navigate = useNavigate();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const [isFeatureUpgradeModalVisible, setIsFeatureUpgradeModalVisible] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
    const [searchText, setSearchText] = useState('');
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);

    // Fetch brands from API
    const { data: brands, isLoading, error } = useGetBrandsQuery({
        jwtToken: user?.jwtToken,
        organisationId: user?.organisation?.id
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Filter brands based on search
    const filteredBrands = useMemo(() => {
        if (!brands) return [];
        if (!searchText) return brands;
        
        return brands.filter(brand => 
            brand.name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [brands, searchText]);

    const handleViewEquity = (brand: IBrand) => {
        navigate('/dashboard/analytics/visualization');
    };

    const handleViewDetails = (brandHash: string) => {
        navigate(`/dashboard/brands/${brandHash}/details`);
    };

    const handleEditBrand = (brand: IBrand) => {
        setSelectedBrand(brand);
        setIsEditModalVisible(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalVisible(false);
        setSelectedBrand(null);
    };


    // Table columns definition
    const columns: ColumnsType<IBrand> = [
        {
            title: 'Brand',
            key: 'brand',
            render: (_, record) => (
                <Space>
                    <Avatar 
                        size={40}
                        src={record.logoUrl}
                        style={{ 
                            backgroundColor: record.primaryColor || '#4338ca',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}
                    >
                        {!record.logoUrl && record.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <div>
                        <Text strong style={{ fontSize: '15px' }}>{record.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            ID: {record.hash}
                        </Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => (
                <Space direction="vertical" size={0}>
                    <Text>{new Date(date).toLocaleDateString()}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {new Date(date).toLocaleTimeString()}
                    </Text>
                </Space>
            ),
            sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        },
        {
            title: 'Status',
            key: 'status',
            render: () => (
                <Tag color="success">Active</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Brand Equity">
                        <Button 
                            type="primary"
                            icon={<BarChartOutlined />}
                            onClick={() => handleViewEquity(record)}
                        >
                            Equity
                        </Button>
                    </Tooltip>
                    <Tooltip title="View Details">
                        <Button 
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetails(record.hash)}
                        >
                            Details
                        </Button>
                    </Tooltip>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: 'edit',
                                    icon: <EditOutlined />,
                                    label: 'Edit Brand',
                                    onClick: () => handleEditBrand(record)
                                },
                                {
                                    type: 'divider',
                                },
                                {
                                    key: 'delete',
                                    icon: <DeleteOutlined />,
                                    label: 'Delete Brand',
                                    danger: true,
                                    disabled: true
                                },
                            ],
                        }}
                    >
                        <Button icon={<MoreOutlined />} />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    // Modern card component
    const BrandCard = ({ brand }: { brand: IBrand }) => (
        <Card
            hoverable
            style={{ 
                height: '100%',
                overflow: 'hidden',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
            }}
            styles={{ body: { padding: 0 } }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
            }}
        >
            {/* Brand Header with Color/Logo */}
            <div 
                style={{ 
                    height: '120px',
                    background: brand.primaryColor ? 
                        `linear-gradient(135deg, ${brand.primaryColor} 0%, ${brand.primaryColor}dd 100%)` : 
                        'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {brand.logoUrl ? (
                    <img 
                        src={brand.logoUrl} 
                        alt={brand.name}
                        style={{ 
                            maxHeight: '60px', 
                            maxWidth: '150px',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                        }}
                    />
                ) : (
                    <div style={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                        {brand.name.charAt(0).toUpperCase()}
                    </div>
                )}
                
                {/* More Options */}
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: 'edit',
                                icon: <EditOutlined />,
                                label: 'Edit Brand',
                                onClick: () => handleEditBrand(brand)
                            },
                            {
                                type: 'divider',
                            },
                            {
                                key: 'delete',
                                icon: <DeleteOutlined />,
                                label: 'Delete Brand',
                                danger: true,
                                disabled: true
                            },
                        ],
                    }}
                >
                    <Button 
                        icon={<MoreOutlined />} 
                        type="text"
                        style={{ 
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.2)'
                        }}
                    />
                </Dropdown>
            </div>

            {/* Brand Info */}
            <div style={{ padding: '20px' }}>
                <Title level={4} style={{ margin: '0 0 8px 0' }}>
                    {brand.name}
                </Title>
                
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CalendarOutlined style={{ color: '#6b7280' }} />
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Created {new Date(brand.createdAt).toLocaleDateString()}
                        </Text>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BuildOutlined style={{ color: '#6b7280' }} />
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            ID: {brand.hash}
                        </Text>
                    </div>
                </Space>

                {/* Actions */}
                <Space style={{ width: '100%', marginTop: '24px' }} direction="vertical" size={8}>
                    <Button 
                        type="primary"
                        block
                        icon={<BarChartOutlined />}
                        onClick={() => handleViewEquity(brand)}
                        style={{ borderRadius: '8px' }}
                    >
                        View Brand Equity
                    </Button>
                    <Button 
                        block
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(brand.hash)}
                        style={{ borderRadius: '8px' }}
                    >
                        View Details
                    </Button>
                </Space>
            </div>
        </Card>
    );

    if (isLoading) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ 
                    padding: 24, 
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Spin size="large" />
                </div>
            </Content>
        );
    }

    if (error) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ 
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}>
                    <Alert 
                        type="error" 
                        message="Failed to load brands" 
                        description="There was an error loading your brands. Please try again later." 
                    />
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    {
                        title: user.organisation?.name || intl.formatMessage({ id: 'common.platformName' })
                    },
                    {
                        title: intl.formatMessage({ id: 'section.brands' })
                    }
                ]}
            />
            
            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                {/* Header Section */}
                <div style={{ marginBottom: 24 }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginBottom: 16
                    }}>
                        <div>
                            <Title level={2} style={{ margin: 0 }}>
                                {intl.formatMessage({ id: 'section.brands' })}
                            </Title>
                            <Text type="secondary">
                                Manage and monitor your brand portfolio
                            </Text>
                        </div>
                    </div>

                    {/* Controls Bar */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap'
                    }}>
                        <Search
                            placeholder="Search brands..."
                            allowClear
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={setSearchText}
                            style={{ width: 300 }}
                            prefix={<SearchOutlined />}
                        />
                        
                        <Button.Group>
                            <Button
                                type={viewMode === 'grid' ? 'primary' : 'default'}
                                icon={<AppstoreOutlined />}
                                onClick={() => setViewMode('grid')}
                            >
                                Grid View
                            </Button>
                            <Button
                                type={viewMode === 'table' ? 'primary' : 'default'}
                                icon={<TableOutlined />}
                                onClick={() => setViewMode('table')}
                            >
                                Table View
                            </Button>
                        </Button.Group>
                    </div>
                </div>
                
                {/* Content Section */}
                {!filteredBrands || filteredBrands.length === 0 ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '80px 40px',
                        background: '#f9fafb',
                        borderRadius: '12px',
                        border: '2px dashed #e5e7eb'
                    }}>
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <Space direction="vertical" size={8}>
                                    <Text style={{ fontSize: '16px', color: '#4b5563' }}>
                                        {searchText ? 'No brands found matching your search' : intl.formatMessage({ id: 'brands.noBrands' })}
                                    </Text>
                                    <Text type="secondary">
                                        {searchText ? 'Try adjusting your search terms' : 'Your brands will appear here once configured'}
                                    </Text>
                                </Space>
                            }
                        >
                        </Empty>
                    </div>
                ) : viewMode === 'grid' ? (
                    <Row gutter={[16, 16]}>
                        {filteredBrands.map((brand) => (
                            <Col xs={24} sm={12} lg={8} xl={6} key={brand.id}>
                                <BrandCard brand={brand} />
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={filteredBrands}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} brands`,
                        }}
                        style={{ 
                            backgroundColor: 'white',
                            borderRadius: '8px',
                        }}
                    />
                )}
            </div>


            <FeatureUpgradeModal
                visible={isFeatureUpgradeModalVisible}
                onClose={() => setIsFeatureUpgradeModalVisible(false)}
                featureName={intl.formatMessage({ id: 'brands.feature.multipleBrands' })}
                featureDescription={intl.formatMessage({ id: 'brands.feature.multipleBrandsDescription' })}
            />

            <EditBrandModal
                visible={isEditModalVisible}
                brand={selectedBrand}
                onClose={handleEditModalClose}
                onSuccess={handleEditModalClose}
            />
        </Content>
    );
}

export default BrandsPage;